import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useForm, SubmitHandler } from "react-hook-form";
import { Modal } from "antd";
import { getApiBaseUrl } from "../../config/runtime";

interface RegisterFormInputs {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormInputs>();

  const onSubmit: SubmitHandler<RegisterFormInputs> = async (data) => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", data.name.trim());
      formData.append("email", data.email.trim().toLowerCase());
      formData.append("password", data.password);

      if (imageFile) {
        formData.append("image", imageFile);
      }

      // Single call — the server creates the account AND dispatches the
      // verification email inline.  The response includes emailSent:boolean.
      const response = await axios.post(`${apiBaseUrl}/register`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      const { emailSent } = response.data as { emailSent: boolean };

      if (emailSent) {
        Modal.success({
          title: "Check your inbox!",
          content:
            "Your account has been created. A verification email has been sent — please click the link within 24 hours to activate your account.",
          onOk() {
            navigate("/login");
          },
        });
      } else {
        // Account created but SMTP failed — user is recoverable via resend.
        Modal.warning({
          title: "Account created — verification email not sent",
          content: (
            <div>
              <p>Your account was created, but we could not send the verification email right now.</p>
              <p style={{ marginTop: 8 }}>
                <strong>What to do next:</strong> go to the{" "}
                <a href="/login" style={{ color: "#2D5A52" }}>Sign In page</a>
                , enter your email and password, and click the{" "}
                <em>"Resend verification email"</em> link that will appear.
              </p>
            </div>
          ),
          onOk() {
            navigate("/login");
          },
        });
      }
    } catch (error) {
      console.error("Error registering user:", error);

      let description = "We could not create your account. Please try again.";

      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; code?: string } | undefined;
        const responseMessage = typeof responseData?.message === "string" ? responseData.message : null;
        const code = responseData?.code;

        if (error.response?.status === 409) {
          if (code === "already_registered_unverified") {
            // Guide them to the login page resend path rather than saying "already exists".
            Modal.warning({
              title: "Account already exists — not yet verified",
              content: (
                <div>
                  <p>{responseMessage ?? "An unverified account with this email already exists."}</p>
                  <p style={{ marginTop: 8 }}>
                    Go to the{" "}
                    <a href="/login" style={{ color: "#2D5A52" }}>Sign In page</a>
                    {" "}and use the <em>"Resend verification email"</em> link to get a fresh link.
                  </p>
                </div>
              ),
              onOk() {
                navigate("/login");
              },
            });
            return;
          }
          description = responseMessage ?? "An account with this email already exists.";
        } else if (error.response?.status === 400) {
          description = responseMessage ?? "Please check the form and try again.";
        } else if (responseMessage) {
          description = responseMessage;
        }
      }

      Modal.error({
        title: "Registration failed",
        content: description,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen flex flex-col px-4 pt-28 pb-12" style={{ backgroundColor: "var(--color-ivory)" }}>
      <form className="w-full max-w-2xl m-auto bg-slate-200 px-4 sm:px-10 py-6 rounded-lg" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-center">
          <label className="form-heading">Create your account</label>
        </div>

        <div className="flex flex-col sm:flex-row sm:space-x-10 w-full">
          <div>
            <div>
              <label className="form-label">Name</label>
              <input className="form-input" {...register("name", { required: "Name is required" })} />
              <div className="h-4">
                {errors.name && <span className="error-msg">{errors.name.message}</span>}
              </div>
            </div>

            <div>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                    message: "Invalid email format",
                  },
                })}
              />
              <div className="h-4">
                {errors.email && <span className="error-msg">{errors.email.message}</span>}
              </div>
            </div>

            <div>
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <div className="h-4">
                {errors.password && <span className="error-msg">{errors.password.message}</span>}
              </div>
            </div>

            <div>
              <label className="form-label">Confirm Password</label>
              <input
                className="form-input"
                type="password"
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) => value === watch("password") || "Passwords do not match",
                })}
              />
              <div className="h-4">
                {errors.confirmPassword && (
                  <span className="error-msg">{errors.confirmPassword.message}</span>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="mt-6">
              {imagePreview ? (
                <img src={imagePreview} alt="Profile Preview" height={120} width={120} />
              ) : (
                <div className="h-32 w-32 text-slate-400 bg-slate-50 flex justify-center items-center">
                  Profile Image
                </div>
              )}
              <label className="form-label">Profile Image (optional)</label>
              <div className="flex flex-col">
                <input
                  type="file"
                  accept="image/*"
                  className="text-[12px] mt-2"
                  onChange={handleImageUpload}
                />
              </div>
            </div>
          </div>
        </div>

        <button className="form-button" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Creating account..." : "Register"}
        </button>

        <div className="mt-4 text-center text-sm text-slate-600">
          Already have an account?{" "}
          <button
            type="button"
            className="font-medium text-slate-800 underline"
            onClick={() => navigate("/login")}
          >
            Sign in
          </button>
        </div>
      </form>
    </div>
  );
};

export default Register;
